import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '@/store'
import { useGetInvitationQuery, useAcceptInvitationMutation } from '@/redux/marketerApi'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import {
  Loader2,
  XCircle,
  ArrowLeft,
  CheckCircle,
  Image as ImageIcon,
  UserPlus,
  LogIn,
} from 'lucide-react'
import { toast } from 'sonner'
import { brandsApi, Brand } from '@/lib/api/brands'
import BrandCreateStep from '@/components/invitation/BrandCreateStep'

type FlowStep = 'select-brand' | 'create-brand' | 'confirm'

const AcceptInvitation = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { currentUser } = useSelector((state: RootState) => state.user)

  const {
    data: invitation,
    isLoading: isInvitationLoading,
    error,
  } = useGetInvitationQuery(token || '', { skip: !token })

  const [acceptInvitation, { isLoading: isAccepting }] = useAcceptInvitationMutation()

  // Brands
  const [brands, setBrands] = useState<Brand[]>([])
  const [brandsLoading, setBrandsLoading] = useState(true)

  // Flow state
  const [step, setStep] = useState<FlowStep>('select-brand')
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null)
  const [campaignTitle, setCampaignTitle] = useState('')

  const returnTo = encodeURIComponent(`/invitation/${token}`)

  // Set default campaign title — prefer the collaboration's own title set by the athlete
  useEffect(() => {
    if (invitation && !campaignTitle) {
      setCampaignTitle(
        invitation.collaboration?.title ||
          `${invitation.inviter?.name || 'Collaborator'} Collaboration`,
      )
    }
  }, [campaignTitle, invitation])

  // Load brands
  useEffect(() => {
    if (!currentUser) return
    brandsApi
      .getBrands()
      .then(({ data }) => {
        setBrands(data)
        // If no brands, skip straight to creation
        if (data.length === 0) {
          setStep('create-brand')
        }
      })
      .catch(() => toast.error('Failed to load brands'))
      .finally(() => setBrandsLoading(false))
  }, [currentUser])

  const handleAccept = async () => {
    if (!token || !selectedBrand) return
    try {
      await acceptInvitation({
        token,
        waveTitle: campaignTitle,
        brandId: selectedBrand.id,
      }).unwrap()
      toast.success('Invitation accepted! Campaign created.')
      navigate('/campaigns')
    } catch (err: any) {
      console.error('Failed to accept:', err)
      toast.error(err.data?.error || 'Failed to accept invitation')
    }
  }

  // Unauthenticated landing — show invitation preview with sign-in / sign-up CTAs
  if (!currentUser && !isInvitationLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen relative p-4">
        <div className="fixed inset-0 -z-10 bg-[#F4F7EF]" />
        <Card className="w-full max-w-md rounded-[28px] border-0 bg-[#FFFFFF] py-4 text-[#0F172A] shadow-[0_20px_60px_rgba(16,24,40,0.08)] sm:px-4">
          <CardHeader className="text-center pb-4">
            <div className="flex justify-center mb-3">
              <img
                src="https://adthlete.us-iad-1.linodeobjects.com/public/adthlete_logomark.png"
                alt="Athios"
                className="w-10 h-10"
              />
            </div>
            {error || !invitation ? (
              <>
                <CardTitle className="text-xl font-bold">Invitation Not Found</CardTitle>
                <CardDescription className="text-[#374151]">
                  This invitation may have expired or does not exist.
                </CardDescription>
              </>
            ) : (
              <>
                <CardTitle className="text-xl font-bold">You've been invited!</CardTitle>
                <CardDescription className="text-[#374151]">
                  <strong className="text-[#0F172A]">{invitation.inviter?.name}</strong> wants to
                  collaborate with you on Athios.
                </CardDescription>
              </>
            )}
          </CardHeader>

          {invitation && !error && (
            <CardContent className="space-y-4">
              {/* Collaboration summary */}
              <div className="rounded-lg bg-[#F9FAFB] border border-[#E5E7EB] divide-y divide-[#E5E7EB]">
                {invitation.collaboration?.title && (
                  <div className="flex justify-between items-center px-4 py-3 text-sm">
                    <span className="text-[#4B5563]">Campaign</span>
                    <span className="font-medium">{invitation.collaboration.title}</span>
                  </div>
                )}
                {invitation.collaboration?.brandName && (
                  <div className="flex justify-between items-center px-4 py-3 text-sm">
                    <span className="text-[#4B5563]">Brand</span>
                    <span className="font-medium">{invitation.collaboration.brandName}</span>
                  </div>
                )}
                {invitation.collaboration?.offerDetails?.deliverables && (
                  <div className="flex justify-between items-center px-4 py-3 text-sm">
                    <span className="text-[#4B5563]">Deliverables</span>
                    <span className="font-medium">
                      {invitation.collaboration.offerDetails.deliverables}
                    </span>
                  </div>
                )}
                {invitation.collaboration?.paymentAmount != null && (
                  <div className="flex justify-between items-center px-4 py-3 text-sm">
                    <span className="text-[#4B5563]">Payment</span>
                    <span className="font-medium text-[#3F7A0A]">
                      ${invitation.collaboration.paymentAmount}
                    </span>
                  </div>
                )}
              </div>

              <p className="text-sm text-[#4B5563] text-center">
                Sign in or create an account to accept this collaboration.
              </p>

              <div className="grid grid-cols-2 gap-3">
                <Link
                  to={`/signup?returnTo=${returnTo}&email=${encodeURIComponent(invitation.email || '')}`}
                >
                  <Button
                    variant="outline"
                    className="w-full bg-transparent border-[#E5E7EB] text-[#0F172A] hover:bg-[#F3F4F6]"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Create Account
                  </Button>
                </Link>
                <Link
                  to={`/signin?returnTo=${returnTo}&email=${encodeURIComponent(invitation.email || '')}`}
                >
                  <Button className="w-full bg-[#C0FD72] hover:bg-[#b0ef62] text-black font-semibold">
                    <LogIn className="h-4 w-4 mr-2" />
                    Sign In
                  </Button>
                </Link>
              </div>
            </CardContent>
          )}

          {(!invitation || error) && (
            <CardContent>
              <Button
                className="w-full bg-[#F3F4F6] hover:bg-[#F3F4F6] text-[#0F172A]"
                onClick={() => navigate('/')}
              >
                Go Home
              </Button>
            </CardContent>
          )}
        </Card>
      </div>
    )
  }

  // Loading state
  if (isInvitationLoading || !currentUser || brandsLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F4F7EF]">
        <Loader2 className="h-8 w-8 animate-spin text-[#3F7A0A]" />
      </div>
    )
  }

  // Error state
  if (error || !invitation) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F4F7EF] px-4 text-[#0F172A]">
        <div className="w-full max-w-md rounded-[28px] bg-[#FFFFFF] px-6 py-10 text-center shadow-[0_20px_60px_rgba(16,24,40,0.08)]">
          <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-2">Invitation Not Found</h1>
          <p className="text-[#4B5563]">This invitation may have expired or does not exist.</p>
          <Button
            className="mt-6 h-12 rounded-xl bg-[#C0FD72] px-6 font-semibold text-[#0F172A] hover:bg-[#B4F25F]"
            onClick={() => navigate('/')}
          >
            Go Home
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center min-h-screen relative p-4">
      <div className="fixed inset-0 -z-10 bg-[#F4F7EF]" />

      <Card className="w-full max-w-md rounded-[28px] border-0 bg-[#FFFFFF] py-4 text-[#0F172A] shadow-[0_20px_60px_rgba(16,24,40,0.08)] sm:px-4">
        <CardHeader className="text-center pb-4">
          <div className="flex justify-center mb-3">
            <div className="h-14 w-14 bg-[#C0FD72]/20 rounded-full flex items-center justify-center">
              <CheckCircle className="h-7 w-7 text-[#3F7A0A]" />
            </div>
          </div>
          <CardTitle className="text-xl font-bold">Accept Collaboration</CardTitle>
          <CardDescription className="text-[#374151]">
            <strong>{invitation.inviter?.name}</strong> has invited you to collaborate.
          </CardDescription>
        </CardHeader>

        <CardContent>
          {/* Step: select-brand */}
          {step === 'select-brand' && (
            <div className="space-y-4">
              <p className="text-sm text-[#4B5563]">
                Which brand will be representing this collaboration?
              </p>
              <div className="grid grid-cols-1 gap-2">
                {brands.map((brand) => (
                  <button
                    key={brand.id}
                    type="button"
                    onClick={() => setSelectedBrand(brand)}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                      selectedBrand?.id === brand.id
                        ? 'border-[#C0FD72] bg-[#C0FD72]/10'
                        : 'border-[#E5E7EB] bg-[#F9FAFB] hover:border-[#D1D5DB]'
                    }`}
                  >
                    <div className="h-10 w-10 rounded flex items-center justify-center overflow-hidden bg-[#F3F4F6] shrink-0">
                      {brand.logo ? (
                        <img
                          src={brand.logo}
                          alt={brand.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <ImageIcon className="h-5 w-5 text-[#4B5563]" />
                      )}
                    </div>
                    <span className="font-medium text-[#0F172A]">{brand.name}</span>
                    {selectedBrand?.id === brand.id && (
                      <CheckCircle className="h-4 w-4 text-[#3F7A0A] ml-auto shrink-0" />
                    )}
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={() => setStep('create-brand')}
                className="w-full p-3 rounded-lg border border-dashed border-[#E5E7EB] text-[#4B5563] hover:border-foreground/40 hover:text-[#0F172A] text-sm transition-colors"
              >
                + Create New Brand
              </button>

              <Button
                className="w-full bg-[#C0FD72] hover:bg-[#b0ef62] text-black font-semibold"
                disabled={!selectedBrand}
                onClick={() => setStep('confirm')}
              >
                Continue
              </Button>
            </div>
          )}

          {/* Step: create-brand */}
          {step === 'create-brand' && (
            <BrandCreateStep
              onCreated={(brand) => {
                setBrands((prev) => [...prev, brand])
                setSelectedBrand(brand)
                setStep('confirm')
              }}
              onBack={brands.length > 0 ? () => setStep('select-brand') : undefined}
            />
          )}

          {/* Step: confirm */}
          {step === 'confirm' && selectedBrand && (
            <div className="space-y-5">
              {/* Selected brand */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-[#F9FAFB] border border-[#E5E7EB]">
                <div className="h-12 w-12 rounded overflow-hidden bg-[#F3F4F6] shrink-0">
                  {selectedBrand.logo ? (
                    <img
                      src={selectedBrand.logo}
                      alt={selectedBrand.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center">
                      <ImageIcon className="h-5 w-5 text-[#4B5563]" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-[#4B5563]">Representing</p>
                  <p className="font-semibold text-[#0F172A] truncate">{selectedBrand.name}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setStep(brands.length > 1 ? 'select-brand' : 'create-brand')}
                  className="text-xs text-[#4B5563] hover:text-[#0F172A] underline shrink-0"
                >
                  Change
                </button>
              </div>

              {/* Collaboration details */}
              <div className="space-y-3 pt-2 border-t border-[#E5E7EB]">
                <div className="flex justify-between text-sm">
                  <span className="text-[#4B5563]">Collaborator</span>
                  <span>{invitation.inviter?.name}</span>
                </div>
                {invitation.collaboration?.offerDetails?.deliverables && (
                  <div className="flex justify-between text-sm">
                    <span className="text-[#4B5563]">Deliverables</span>
                    <span>{invitation.collaboration.offerDetails.deliverables}</span>
                  </div>
                )}
                {invitation.collaboration?.paymentAmount != null && (
                  <div className="flex justify-between text-sm">
                    <span className="text-[#4B5563]">Payment</span>
                    <span>${invitation.collaboration.paymentAmount}</span>
                  </div>
                )}
              </div>

              {/* Campaign name */}
              <div className="space-y-2">
                <Label className="text-[#0F172A] text-sm">Campaign Name</Label>
                <Input
                  value={campaignTitle}
                  onChange={(e) => setCampaignTitle(e.target.value)}
                  className="bg-[#F9FAFB] border-[#E5E7EB] text-[#0F172A]"
                />
                <p className="text-xs text-[#4B5563]">
                  A new Campaign will be created in your organization for this collaboration.
                </p>
              </div>

              <div className="flex gap-3 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  className="bg-transparent border-[#E5E7EB] text-[#0F172A] hover:bg-[#F3F4F6]"
                  onClick={() => setStep('select-brand')}
                >
                  <ArrowLeft className="h-4 w-4 mr-1.5" />
                  Back
                </Button>
                <Button
                  className="flex-1 bg-[#C0FD72] hover:bg-[#b0ef62] text-black font-semibold"
                  onClick={handleAccept}
                  disabled={isAccepting || !campaignTitle.trim()}
                >
                  {isAccepting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Setting up Campaign...
                    </>
                  ) : (
                    'Accept & Create Campaign'
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default AcceptInvitation
